import { useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import {
  Close,
  OutlinedFlag,
} from '@mui/icons-material';

import type {
  ConversationMode,
} from '../services/conversationPersistence';

import {
  ProblemReportRequestError,
  ReportCategory,
  submitProblemReport,
} from '../services/problemReports';

import './ReportProblemDialog.css';

interface ReportProblemDialogProps {
  language: string;
  mode: ConversationMode;
}

const CATEGORY_OPTIONS: Array<{
  value: ReportCategory;
  label: string;
}> = [
  {
    value: 'incorrect_response',
    label: 'La respuesta de RETZIA fue incorrecta',
  },
  {
    value: 'misunderstood_input',
    label: 'RETZIA no entendió mi mensaje o audio',
  },
  {
    value: 'technical_problem',
    label: 'Hubo un problema técnico',
  },
  {
    value: 'other',
    label: 'Otro',
  },
];

export const ReportProblemDialog = ({
  language,
  mode,
}: ReportProblemDialogProps) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] =
    useState<ReportCategory | ''>('');
  const [comment, setComment] = useState('');
  const [includeConversation, setIncludeConversation] =
    useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);
  const [submittedReportId, setSubmittedReportId] =
    useState<number | null>(null);

  const resetForm = () => {
    setCategory('');
    setComment('');
    setIncludeConversation(true);
    setSubmitting(false);
    setErrorMessage(null);
    setSubmittedReportId(null);
  };

  const handleOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
    }
  };

  const handleSubmit = async () => {
    if (!category || submitting) {
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await submitProblemReport({
        language,
        mode,
        category,
        comment,
        includeConversation,
      });

      setSubmittedReportId(result.id);
    } catch (error) {
      if (
        error instanceof ProblemReportRequestError &&
        error.status === 429
      ) {
        setErrorMessage(
          'Has enviado varios reportes recientemente. Espera unos minutos antes de intentarlo nuevamente.'
        );
      } else if (
        error instanceof ProblemReportRequestError &&
        (error.status === 401 || error.status === 403)
      ) {
        setErrorMessage(
          'Tu sesión ya no está disponible. Abre nuevamente RETZIA desde tu plataforma RETZ.'
        );
      } else {
        setErrorMessage(
          'No pudimos enviar el reporte. Revisa tu conexión e inténtalo nuevamente.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <IconButton
        className="chat-feedback-btn"
        aria-label="Reportar un problema"
        title="Reportar un problema"
        onClick={handleOpen}
      >
        <OutlinedFlag />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => handleClose()}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: 'retzia-report-dialog',
        }}
        aria-labelledby="retzia-report-title"
      >
        <DialogTitle
          id="retzia-report-title"
          className="retzia-report-dialog__title"
        >
          Reportar un problema

          <IconButton
            className="retzia-report-dialog__close"
            aria-label="Cerrar"
            onClick={handleClose}
            disabled={submitting}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className="retzia-report-dialog__content">
          {submittedReportId !== null ? (
            <div className="retzia-report-dialog__success">
              <Alert severity="success">
                Tu reporte fue enviado correctamente.
              </Alert>

              <Typography variant="body1">
                Gracias por ayudarnos a mejorar RETZIA.
                Tu folio de reporte es #{submittedReportId}.
              </Typography>
            </div>
          ) : (
            <>
              <Typography
                variant="body2"
                className="retzia-report-dialog__intro"
              >
                Cuéntanos qué ocurrió. No necesitas copiar
                manualmente la conversación.
              </Typography>

              <FormControl
                component="fieldset"
                className="retzia-report-dialog__categories"
              >
                <FormLabel component="legend">
                  ¿Qué tipo de problema encontraste?
                </FormLabel>

                <RadioGroup
                  value={category}
                  onChange={event => {
                    setCategory(
                      event.target.value as ReportCategory
                    );
                  }}
                >
                  {CATEGORY_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <TextField
                label="Cuéntanos más (opcional)"
                value={comment}
                onChange={event => {
                  setComment(
                    event.target.value.slice(0, 2000)
                  );
                }}
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                inputProps={{
                  maxLength: 2000,
                }}
                helperText={`${comment.length}/2000`}
              />

              <div className="retzia-report-dialog__conversation">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeConversation}
                      onChange={event => {
                        setIncludeConversation(
                          event.target.checked
                        );
                      }}
                    />
                  }
                  label="Adjuntar los últimos mensajes de esta conversación"
                />

                <Typography variant="caption">
                  Esto nos permite revisar el contexto exacto
                  del problema. Puedes desactivarlo antes de
                  enviar el reporte.
                </Typography>
              </div>

              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions className="retzia-report-dialog__actions">
          {submittedReportId !== null ? (
            <Button
              variant="contained"
              onClick={handleClose}
            >
              Cerrar
            </Button>
          ) : (
            <>
              <Button
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!category || submitting}
                startIcon={
                  submitting
                    ? (
                      <CircularProgress
                        size={18}
                        color="inherit"
                      />
                    )
                    : undefined
                }
              >
                {submitting
                  ? 'Enviando…'
                  : 'Enviar reporte'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
